import { useEffect, useRef, useState, useCallback } from "react";
import { MessageBubble } from "./MessageBubble";
import { formatDayLabel } from "@/lib/format";
import { Lock, ChevronDown } from "lucide-react";

const GROUP_WINDOW = 5 * 60 * 1000;

export function ChatThread({ messages, isMine, showEncryptionNotice = true, typingLabel, wallpaper, surface = "light", onVoiceTranslate, translationActive = false }) {
  const scrollRef = useRef(null);
  const bottomRef = useRef(null);
  const prevLenRef = useRef(0);
  const atBottomRef = useRef(true);
  const [atBottom, setAtBottom] = useState(true);
  const [newCount, setNewCount] = useState(0);
  const dark = surface === "dark";

  const scrollToBottom = useCallback((behavior = "smooth") => {
    bottomRef.current?.scrollIntoView({ behavior, block: "end" });
    setNewCount(0);
  }, []);

  const handleScroll = () => {
    const el = scrollRef.current;
    if (!el) return;
    const dist = el.scrollHeight - el.scrollTop - el.clientHeight;
    const bottom = dist < 130;
    atBottomRef.current = bottom;
    setAtBottom(bottom);
    if (bottom) setNewCount(0);
  };

  // When the mobile keyboard opens/closes the viewport resizes; if the user
  // was reading the latest messages, keep them pinned to the bottom.
  useEffect(() => {
    const onResize = () => {
      if (atBottomRef.current) scrollToBottom("auto");
    };
    window.addEventListener("resize", onResize);
    window.visualViewport?.addEventListener("resize", onResize);
    return () => {
      window.removeEventListener("resize", onResize);
      window.visualViewport?.removeEventListener("resize", onResize);
    };
  }, [scrollToBottom]);

  useEffect(() => {
    const grew = messages.length > prevLenRef.current;
    const first = prevLenRef.current === 0;
    prevLenRef.current = messages.length;
    if (!grew) return;
    if (atBottom || first) scrollToBottom(first ? "auto" : "smooth");
    else {
      const last = messages[messages.length - 1];
      if (last && !isMine(last)) setNewCount((c) => c + 1);
    }
  }, [messages, atBottom, isMine, scrollToBottom]);

  useEffect(() => {
    if (atBottom && typingLabel) scrollToBottom();
  }, [typingLabel, atBottom, scrollToBottom]);

  const wallStyle = wallpaper
    ? { backgroundColor: "#e1d9ce", backgroundImage: `url(${wallpaper})`, backgroundSize: "300px auto" }
    : undefined;

  const canvasClass = wallpaper ? "" : dark ? "web3-chat-canvas" : "chat-canvas";
  const chipClass = dark ? "bg-white/10 text-gray-300 backdrop-blur" : "bg-white/95 text-[#5b6a64]";

  return (
    <div className="relative flex min-h-0 flex-1 flex-col">
      <div
        ref={scrollRef}
        onScroll={handleScroll}
        className={`sc-scroll flex-1 overflow-y-auto px-3 py-3 md:px-8 ${canvasClass}`}
        style={wallStyle}
        data-testid="chat-thread"
      >
        <div className="mx-auto flex max-w-3xl flex-col">
          {showEncryptionNotice && (
            <div className={`mx-auto mb-2 max-w-sm rounded-[10px] px-4 py-2 text-center text-[12.5px] leading-[1.55] shadow-sm ${dark ? "bg-white/5 text-gray-400 border border-white/10" : "bg-[#fdf4c5] text-[#5b6a3f]"}`}>
              <Lock className="mr-1 inline h-3 w-3 -translate-y-px" aria-hidden="true" />
              Messages and calls are end-to-end encrypted. Only people in this chat can read, listen to, or share them.{" "}
              <span className={`font-bold ${dark ? "text-[#2ac67b]" : "text-[#0e8f7e]"}`}>Learn more</span>
            </div>
          )}
          {messages.map((m, i) => {
            const mine = isMine(m);
            const prev = messages[i - 1];
            const day = formatDayLabel(m.created_at);
            const sep = !prev || formatDayLabel(prev.created_at) !== day;
            const grouped =
              prev && !sep && isMine(prev) === mine && prev.sender_id === m.sender_id &&
              new Date(m.created_at) - new Date(prev.created_at) < GROUP_WINDOW;
            return (
              <div key={m.id}>
                {sep && (
                  <div className="sticky top-1 z-10 my-2 flex justify-center">
                    <span className={`rounded-md px-3 py-1 text-[12px] font-semibold shadow-sm ${chipClass}`}>{day}</span>
                  </div>
                )}
                <div className={grouped ? "mt-[3px]" : "mt-2.5"}>
                  <MessageBubble msg={m} mine={mine} tail={!grouped} surface={surface} onVoiceTranslate={onVoiceTranslate} translationActive={translationActive} />
                </div>
              </div>
            );
          })}
          {typingLabel && (
            <div className="mt-2.5 flex justify-start" data-testid="typing-indicator">
              <div className={`flex items-center gap-1 rounded-2xl rounded-tl-md px-3 py-3 shadow-sm ${dark ? "bg-white/[0.06] border border-white/10" : "bg-white"}`}>
                <span className="sc-typing-dot h-2 w-2 rounded-full bg-[#8a938f]" style={{ animationDelay: "0ms" }} />
                <span className="sc-typing-dot h-2 w-2 rounded-full bg-[#8a938f]" style={{ animationDelay: "150ms" }} />
                <span className="sc-typing-dot h-2 w-2 rounded-full bg-[#8a938f]" style={{ animationDelay: "300ms" }} />
              </div>
            </div>
          )}
          <div ref={bottomRef} />
        </div>
      </div>

      {!atBottom && (
        <button
          onClick={() => scrollToBottom()}
          className={`absolute bottom-4 right-4 z-20 flex h-11 w-11 items-center justify-center rounded-full shadow-lg transition-transform hover:scale-105 active:scale-95 ${dark ? "glass text-[#2ac67b]" : "bg-white text-[#54656f] ring-1 ring-black/5"}`}
          data-testid="scroll-to-bottom-button"
          aria-label="Scroll to latest"
        >
          <ChevronDown className="h-5 w-5" aria-hidden="true" />
          {newCount > 0 && (
            <span className="absolute -top-1 -right-1 flex h-5 min-w-[20px] items-center justify-center rounded-full bg-[#2ac67b] px-1 text-[11px] font-bold text-black">
              {newCount}
            </span>
          )}
        </button>
      )}
    </div>
  );
}
